import {
	type InfiniteData,
	type QueryKey,
	type UseInfiniteQueryOptions,
	useQueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";
import { useOnlineStatus } from "web_myplann/Infrastructure/Hooks/useOnlineStatus";

export const limit = 20;

type PageMeta = {
	totalCount: number;
	unreadCount?: number;
	limit?: number | null;
	offset?: number | null;
};

type TotalCountMeta = {
	meta: PageMeta;
};

type GetTotal = (meta: PageMeta | undefined) => number | undefined;

export const makeOffsetInfiniteQueryOptions = (
	getTotal: GetTotal = (meta) => meta?.totalCount,
) =>
	({
		getNextPageParam: (
			lastPage: TotalCountMeta,
			_allPages: TotalCountMeta[],
			lastPageParam: number,
			_allPageParams: number[],
		) => {
			const currentOffset = lastPageParam;

			const pageLimit = lastPage?.meta?.limit ?? limit;
			if (!pageLimit || pageLimit <= 0) return null;

			const nextOffset = currentOffset + pageLimit;

			const total = getTotal(lastPage?.meta);
			if (typeof total === "number" && nextOffset >= total) return null;

			if (nextOffset === lastPageParam) return null;

			return nextOffset;
		},
		initialPageParam: 0,
	}) as const satisfies Partial<
		UseInfiniteQueryOptions<
			TotalCountMeta,
			Error,
			TotalCountMeta,
			QueryKey,
			number
		>
	>;

export type UseResetInfiniteDataOptions = {
	disableReset?: boolean;
};

export const useResetInfiniteData = (
	queryKey: QueryKey,
	options?: UseResetInfiniteDataOptions,
) => {
	const queryClient = useQueryClient();
	const isOnline = useOnlineStatus();
	const first = useRef(0);

	if (first.current !== 0) return;

	first.current = 1;
	if (!isOnline || options?.disableReset) return;

	queryClient.setQueryData<InfiniteData<unknown, number>>(queryKey, (data) =>
		!data?.pages || !data?.pageParams || data.pages.length === 1
			? data
			: ({
					pages: data?.pages?.slice(0, 1),
					pageParams: data?.pageParams?.slice(0, 1),
				} as InfiniteData<unknown, number>),
	);
};
